/* ==========================================================================
   A link somebody sent you

   Opened by a person who may have no account, may be on a borrowed phone, and
   almost certainly arrived from WhatsApp. So: no tabs, no app bar, nothing to
   sign into before you can read it. Just the thing itself, and one door in.

   It shows less than the app does, on purpose. Never an address, never a
   phone number, never a calendar — a link travels further than the person who
   sent it meant it to.
   ========================================================================== */

import { useEffect } from 'react';
import { Avatar, Card, JobChips, SectionHead, Stars } from '../../components/ui';
import { areaLabel } from '../../lib/domain';
import { endTime, fmtDate, fmtTime, money } from '../../lib/time';
import { t } from '../../lib/i18n';
import { dismissShare, loadShare, state } from '../../store';

export function SharedLink() {
  useEffect(() => { void loadShare(); }, []);
  const s = state.share;

  if (!s) {
    return <div className="screen-pad"><span className="sub">Loading…</span></div>;
  }

  return (
    <div className="screen-pad">
      <Card className="tint">
        <b style={{ fontSize: 18 }}>Ozeret</b>
        <span className="sub tight">
          {t('sh_tagline')}
        </span>
      </Card>

      {s.gone ? (
        <Card>
          <b>{t('sh_gone')}</b>
          <span className="sub">
            {s.kind === 'request'
              ? 'The home has already found somebody, or taken the job down.'
              : s.kind === 'house'
                ? 'That home is not looking for anyone at the moment.'
                : 'That cleaner is not taking work through Ozeret at the moment.'}
          </span>
        </Card>
      ) : s.kind === 'request' ? (
        <SharedRequest r={s.request!} home={s.home!} />
      ) : s.kind === 'house' ? (
        <SharedHome home={s.home!} jobs={s.openJobs ?? []} />
      ) : (
        <SharedCleaner c={s.cleaner!} />
      )}

      <button type="button" className="btn lg block" onClick={dismissShare}>
        {s.gone ? 'Look at Ozeret anyway' : 'Open Ozeret'}
      </button>
      <p className="hint" style={{ textAlign: 'center' }}>
        You will need an invite code to join. Whoever sent you this can give you one.
      </p>
    </div>
  );
}

function SharedRequest({ r, home }: { r: Record<string, any>; home: Record<string, any> }) {
  return (
    <Card recur={!!r.recurring}>
      <div className="row top">
        <Avatar p={{ initials: home.initials, hue: home.hue }} />
        <div className="col grow">
          <div className="row between">
            <span className="name">{home.displayName}</span>
            <span className="rate">{money(r.rate)}<small>{t('perhour')}</small></span>
          </div>
          <span className="sub">
            {home.homeType} · {home.bedrooms} bed · {areaLabel(home.area)}
          </span>
          {home.rating?.count > 0 && (
            <div className="row" style={{ gap: 6, marginTop: 3 }}>
              <Stars value={home.rating.overall} />
              <span className="sub tight">{home.rating.count} {t('reviews')}</span>
            </div>
          )}
        </div>
      </div>

      <SectionHead title={t('when')} />
      <b>
        {fmtDate(r.date, { long: true })}, {fmtTime(r.start)}–{endTime(r.start, r.hours)}
        {' · '}{r.hours}h · {money(r.rate * r.hours)} total
      </b>

      <SectionHead title={t('h_whattheyneed')} />
      <div className="chips"><JobChips ids={r.jobIds} /></div>
      {r.notes && <span className="sub">{r.notes}</span>}
    </Card>
  );
}

/** A home, shared as a home rather than as one job -- what a cleaner sends a
 *  friend, or what a home sends to somebody it wants to work with.
 *
 *  Same rule as everywhere else: the town, never the street. The address is
 *  earned by being booked, and a link cannot be a way round that. */
function SharedHome({ home, jobs }: { home: Record<string, any>; jobs: any[] }) {
  return (
    <Card>
      <div className="row top">
        <Avatar p={{ initials: home.initials, hue: home.hue }} size="lg" />
        <div className="col grow">
          <span className="name" style={{ fontSize: 18 }}>{home.displayName}</span>
          <span className="sub">
            {home.homeType} · {home.bedrooms} bed · {areaLabel(home.area)}
          </span>
          {home.ratings?.count > 0 && (
            <div className="row" style={{ gap: 6, marginTop: 3 }}>
              <Stars value={home.ratings.overall} />
              <span className="sub tight">
                {home.ratings.count} cleaners rated them
              </span>
            </div>
          )}
        </div>
      </div>

      {home.about && (
        <>
          <SectionHead title={t('h_thehome')} />
          <span className="sub">{home.about}</span>
        </>
      )}

      <div className="chips">
        {home.suppliesProvided && <span className="chip">{t('sh_supplies')}</span>}
        {home.kosherHome ? <span className="chip">{t('j_kosher')}</span> : null}
        {home.kids > 0 && <span className="chip">{home.kids} kids</span>}
        {home.pets && home.pets !== 'none' && <span className="chip">{home.pets}</span>}
      </div>

      <SectionHead title={t('h_whattheyneed')} />
      {jobs.length === 0 ? (
        <span className="sub">{t('sh_nothingopen')}</span>
      ) : (
        jobs.map((j) => (
          <div className="col" key={j.id} style={{ gap: 3, marginBottom: 8 }}>
            <b style={{ fontSize: 14.5 }}>
              {fmtDate(j.date, { long: true })}, {fmtTime(j.start)}–{endTime(j.start, j.hours)}
            </b>
            <span className="sub tight">
              {j.hours}h · {money(j.rate)}{t('perhour')} · {money(j.rate * j.hours)}
              {j.recurring ? ' · ' + t('everyweek') : ''}
            </span>
            <div className="chips"><JobChips ids={j.jobIds} max={4} /></div>
          </div>
        ))
      )}
    </Card>
  );
}

function SharedCleaner({ c }: { c: Record<string, any> }) {
  return (
    <Card>
      <div className="row top">
        <Avatar p={{ initials: c.initials, hue: c.hue }} size="lg" />
        <div className="col grow">
          <div className="row between">
            <span className="name" style={{ fontSize: 18 }}>{c.name}</span>
            <span className="rate">{money(c.rate)}<small>{t('perhour')}</small></span>
          </div>
          <span className="sub">{c.headline}</span>
          {c.ratings?.count > 0 && (
            <div className="row" style={{ gap: 6, marginTop: 3 }}>
              <Stars value={c.ratings.overall} />
              <span className="sub tight">{c.ratings.count} {t('reviews')}</span>
            </div>
          )}
        </div>
      </div>

      {c.bio && (
        <>
          <SectionHead title={t('h_intheirwords')} />
          <span className="sub">{c.bio}</span>
        </>
      )}

      <SectionHead title={t('h_howtheywork')} />
      <span className="sub">
        {c.yearsExp} {t('years')} · {c.minHours}h {t('minimum')}
      </span>

      <SectionHead title={t('areas')} />
      <div className="chips">
        {(c.areas ?? []).map((a: string) => (
          <span className="chip" key={a}>{areaLabel(a)}</span>
        ))}
      </div>

      {(c.specialties ?? []).length > 0 && (
        <>
          <SectionHead title={t('specialties')} />
          <div className="chips"><JobChips ids={c.specialties} /></div>
        </>
      )}
    </Card>
  );
}
